import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, FloppyDisk, ArrowCounterClockwise, Trash, Crown } from "@phosphor-icons/react";
import { toast } from "sonner";

export const money = (n, dp = 2) =>
  n == null || Number.isNaN(Number(n)) ? "—" : new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: dp, maximumFractionDigits: dp }).format(Number(n));
export const fmtDate = (s) => (s ? new Date(`${s.slice(0, 10)}T12:00:00`).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—");
export const errToast = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
export const today = () => { const n = new Date(); return new Date(n.getTime() - n.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
export const thisMonth = () => today().slice(0, 7);

export function useIsPro() {
  const { user } = useAuth();
  return user?.plan === "pro" || user?.role === "admin";
}

export function FinShell({ title, subtitle, children, testId }) {
  return (
    <div className="animate-fade-up space-y-6" data-testid={testId}>
      <Link to="/finance" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="fin-back-link">
        <ArrowLeft className="h-4 w-4" /> Financial Centre
      </Link>
      <div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1 text-sm md:text-base max-w-2xl">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function Card({ title, subtitle, right, children, testId, className = "" }) {
  return (
    <section className={`rounded-xl border border-border bg-card p-5 md:p-6 ${className}`} data-testid={testId}>
      {(title || right) && (
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            {title && <h3 className="font-bold text-base md:text-lg">{title}</h3>}
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function NumField({ id, label, value, onChange, hint, type = "number", step = "0.01", ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Input id={id} data-testid={id} type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-secondary/40" {...rest} />
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function SelectField({ id, label, value, onChange, options }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <select id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-md border border-input bg-secondary/40 px-3 text-sm">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

export function Stat({ label, value, tone, testId, sub }) {
  const c = tone === "good" ? "text-emerald-400" : tone === "bad" ? "text-red-400" : "text-foreground";
  return (
    <div className="rounded-lg bg-secondary/40 p-4" data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-xl md:text-2xl font-black mt-1 ${c}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export function Assumptions({ children }) {
  return <p className="text-xs text-muted-foreground leading-relaxed" data-testid="fin-assumptions"><strong>Estimates only.</strong> {children}</p>;
}

export function useProfiles(kind) {
  const isPro = useIsPro();
  const q = useQuery({ queryKey: ["fin-profiles", kind], queryFn: async () => (await api.get("/finance/profiles", { params: { kind } })).data, enabled: isPro });
  return { ...q, isPro };
}

/** Load / save / delete / reset bar shared by every saved calculator. */
export function SavedBar({ kind, selected, onSelect, inputs, onReset, defaultName }) {
  const qc = useQueryClient();
  const { data: profiles = [], isPro } = useProfiles(kind);
  const [name, setName] = useState("");
  const current = profiles.find((p) => p.id === selected);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["fin-profiles", kind] }); qc.invalidateQueries({ queryKey: ["fin-profile"] }); qc.invalidateQueries({ queryKey: ["fin-summary"] }); };
  const save = async () => {
    try {
      if (current) {
        await api.put(`/finance/profiles/${current.id}`, { kind, name: name || current.name, inputs });
        toast.success("Saved");
      } else {
        const d = (await api.post("/finance/profiles", { kind, name: name || defaultName, inputs })).data;
        onSelect(d.id, d.inputs);
        toast.success("Saved to your Financial Centre");
      }
      refresh();
    } catch (e) { errToast(e); }
  };
  const del = async () => {
    if (!current || !window.confirm(`Delete "${current.name}" and its payment history?`)) return;
    try { await api.delete(`/finance/profiles/${current.id}`); onSelect(null); onReset(); refresh(); toast.success("Deleted"); } catch (e) { errToast(e); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-card p-3" data-testid="fin-saved-bar">
      {isPro ? (
        <>
          <select data-testid="fin-saved-select" value={selected || ""} onChange={(e) => { const p = profiles.find((x) => x.id === e.target.value); onSelect(p?.id || null, p?.inputs); setName(""); }}
            className="h-9 rounded-md border border-input bg-secondary/40 px-2 text-sm min-w-[180px]">
            <option value="">New calculation</option>
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <Input data-testid="fin-save-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={current?.name || defaultName} className="h-9 w-48 bg-secondary/40" />
          <Button size="sm" data-testid="fin-save-btn" onClick={save}><FloppyDisk className="h-4 w-4 mr-1" /> {current ? "Save changes" : "Save"}</Button>
          {current && <Button size="sm" variant="ghost" data-testid="fin-delete-btn" onClick={del}><Trash className="h-4 w-4 text-red-400" /></Button>}
        </>
      ) : (
        <Button size="sm" variant="outline" data-testid="fin-save-btn" onClick={() => window.dispatchEvent(new CustomEvent("pro-required", { detail: "Saving calculations and payment history is a MyPayAlert Pro feature." }))}>
          <Crown className="h-4 w-4 mr-1 text-primary" weight="fill" /> Save (Pro)
        </Button>
      )}
      <Button size="sm" variant="ghost" className="ml-auto" data-testid="fin-reset-btn" onClick={() => { onSelect(null); onReset(); }}>
        <ArrowCounterClockwise className="h-4 w-4 mr-1" /> Reset
      </Button>
    </div>
  );
}
